// Ready-to-paste share message builders for a Growth Campaign launch.
// Consultant-side outbound copy — the consultant pastes this into WhatsApp /
// LinkedIn / Email when driving traffic to their campaign.
//
// Pack 6 revision — voice notes:
//   • Professional, not marketing. No "quick share", "Hi —", emojis.
//   • Encourage a Discovery Meeting explicitly, not just "walk through results".
//   • Reinforce consultant credibility (name + company + expertise line).
//   • Always include the scoped campaign URL as the primary CTA.
//   • Keep WhatsApp under ~450 chars so the send preview isn't truncated.

const first = (name) => (name || "").trim().split(" ")[0] || "there";

/** Small helper: build a one-line credibility phrase from expertise[] */
const credibility = (consultant) => {
  const exp = Array.isArray(consultant?.expertise) ? consultant.expertise.filter(Boolean) : [];
  if (exp.length === 0) return "";
  if (exp.length === 1) return exp[0];
  if (exp.length === 2) return `${exp[0]} and ${exp[1]}`;
  return `${exp[0]}, ${exp[1]} and ${exp[2]}`;
};

export function whatsappTemplate(consultant, campaign, url) {
  const name = consultant?.name || "your BizPulse consultant";
  const firm = consultant?.company ? ` at ${consultant.company}` : "";
  const audience = campaign?.target_executive
    ? `${campaign.target_executive}s`
    : "senior leaders";
  const industry = campaign?.industry || "your industry";
  const country = campaign?.country ? ` in ${campaign.country}` : "";
  return (
    `Hello,\n\n` +
    `I run a confidential Business Growth Discovery™ for ${audience} in ${industry}${country}. ` +
    `It takes 8–10 minutes and delivers your Business Growth Index™ plus the top three ` +
    `opportunities across your ${campaign?.function || "operations"} function.\n\n` +
    `Take it here:\n${url}\n\n` +
    `If any of the findings resonate, I'd be glad to schedule a 30-minute Discovery Meeting.\n\n` +
    `${name}${firm}`
  );
}

export function linkedinTemplate(consultant, campaign, url) {
  const audience = campaign?.target_executive
    ? `${campaign.target_executive}s`
    : "senior leaders";
  const industry = campaign?.industry || "the industry";
  const country = campaign?.country ? ` in ${campaign.country}` : "";
  const credLine = credibility(consultant);
  const name = consultant?.name || "";
  const firm = consultant?.company || "";
  return (
    `${audience} in ${industry}${country} — how confident are you that your ` +
    `${campaign?.function || "business"} function is set up for the next 12 months?\n\n` +
    `I have opened a confidential Business Growth Discovery™ built specifically for this segment. ` +
    `It scores your maturity across the categories that matter most, ranks the top three ` +
    `opportunities you can act on this quarter, and delivers an Executive Growth Report™ you own outright.\n\n` +
    `Start here → ${url}\n\n` +
    `I personally review every report before it's released. If any finding resonates, ` +
    `I welcome a 30-minute Discovery Meeting — no obligation.\n\n` +
    (credLine ? `— ${name}${firm ? `, ${firm}` : ""}\n${credLine}\n\n` : `— ${name}${firm ? `, ${firm}` : ""}\n\n`) +
    `#${(campaign?.industry || "Growth").replace(/\s+/g, "")} ` +
    `#${(campaign?.function || "Advisory").replace(/\s+/g, "")}`
  );
}

export function emailTemplate(consultant, campaign, url) {
  const audience = campaign?.target_executive || "senior leaders";
  const industry = campaign?.industry || "your industry";
  const country = campaign?.country ? ` in ${campaign.country}` : "";
  const fn = campaign?.function || "business";
  const credLine = credibility(consultant);
  const name = consultant?.name || "";
  const firm = consultant?.company || "";
  return {
    subject: `A confidential Business Growth Discovery™ for ${audience} in ${industry}`,
    body:
      `Hello ${first(audience)},\n\n` +
      `I have opened a confidential Business Growth Discovery™ built specifically for ${audience} ` +
      `in ${industry}${country}.\n\n` +
      `It takes 8–10 minutes and delivers three things: your Business Growth Index™, the top three ` +
      `opportunities to act on this quarter across your ${fn} function, and an Executive Growth Report™ ` +
      `that you own outright.\n\n` +
      `Start here: ${url}\n\n` +
      `I personally review every report before it is released. If any finding resonates, ` +
      `I would welcome a 30-minute Discovery Meeting — no obligation, no sales pitch, just an ` +
      `executive conversation about what the data suggests.\n\n` +
      `Kind regards,\n` +
      `${name}\n` +
      (firm ? `${firm}\n` : "") +
      (credLine ? `${credLine}\n` : ""),
  };
}
